// StreamfinitePage.jsx  ─  Animac Metaverse  ─  Production redesign
// Requires: framer-motion, lucide-react, react-router-dom, react-swipeable
// API utils: anilistQuery, jikanRequest, tmdbRequest, traktRequest  (../utils/apiProxy)
// Env vars: REACT_APP_TMDB_API_KEY, REACT_APP_TRAKT_KEY

import React, {
  useEffect, useMemo, useRef, useState, useCallback, createContext, useContext
} from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Play, X, Star, Film, Tv, RefreshCw,
  Sparkles, Flame, Crown, TrendingUp,
  ChevronRight, Plus,
  Globe
} from 'lucide-react';
import { anilistQuery, jikanRequest, tmdbRequest, traktRequest } from '../utils/apiProxy';
import StreamfinitePreview from '../components/StreamfinitePreview';

/* ═══════════════════════════════════════════════════════════════════
   GLOBAL STYLES
═══════════════════════════════════════════════════════════════════ */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@700;900&display=swap');
    @font-face{font-family:'Azonix';src:url('/fonts/Azonix.otf') format('opentype'),url('/fonts/azonix.woff2') format('woff2');font-display:swap;}
    @font-face{font-family:'Japanese3017';src:url('/fonts/Japanese-3017.ttf') format('truetype');font-display:swap;}
    @font-face{font-family:'Acknowledgement';src:url('/fonts/Acknowledgement.otf') format('opentype');font-display:swap;}
    :root{
      --sf-az:'Azonix','Impact',sans-serif;
      --sf-jp:'Japanese3017','Noto Serif JP',serif;
      --sf-wt:'Acknowledgement','Azonix',sans-serif;
      --sf-ui:-apple-system,'Helvetica Neue',sans-serif;
      --sf-bd:'Georgia',serif;
      --sf-east:#dc2626;--sf-west:#06b6d4;--sf-bg:#07070b;
      --sf-glass:rgba(255,255,255,0.04);--sf-gb:rgba(255,255,255,0.08);
    }
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    html{scroll-behavior:smooth;}
    .sf-root{background:var(--sf-bg);color:#fff;font-family:var(--sf-ui);overflow-x:hidden;min-height:100vh;}
    .sf-wrap{max-width:1280px;margin:0 auto;padding:0 clamp(16px,4vw,52px);}
 
    /* HERO */
    .sf-hero{position:relative;width:100%;height:100svh;height:100vh;min-height:520px;max-height:900px;overflow:hidden;display:flex;flex-direction:column;justify-content:flex-end;background:var(--sf-bg);}
    .sf-hero-bg-wrap{position:absolute;inset:-40px;z-index:0;}
    .sf-hero-bg-desk{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:center 20%;}
    .sf-hero-bg-mob{display:none;position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:center top;}
    .sf-hero-radial{position:absolute;inset:0;z-index:1;background:radial-gradient(ellipse 55% 60% at 62% 38%,transparent 0%,transparent 30%,rgba(7,7,11,0.25) 55%,rgba(7,7,11,0.75) 80%,rgba(7,7,11,0.96) 100%);}
    .sf-hero-edge{position:absolute;inset:0;z-index:2;box-shadow:inset 80px 0 80px rgba(7,7,11,0.92),inset -10px 0 60px rgba(7,7,11,0.2),inset 0 80px 80px rgba(7,7,11,0.6);}
    .sf-hero-bottom{position:absolute;inset:0;z-index:3;background:linear-gradient(to top,rgba(7,7,11,1) 0%,rgba(7,7,11,0.7) 22%,rgba(7,7,11,0.2) 45%,transparent 65%);}
    .sf-hero-tint{position:absolute;inset:0;z-index:4;transition:background .9s ease;pointer-events:none;}
    .sf-hero-topbar{position:absolute;top:clamp(16px,3.5vh,28px);left:clamp(16px,5vw,60px);right:clamp(16px,5vw,60px);z-index:10;display:flex;align-items:center;justify-content:space-between;}
    .sf-hero-label{display:inline-flex;align-items:center;gap:7px;padding:4px 13px;border-radius:100px;border:1px solid;backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);background:rgba(0,0,0,.38);font-family:var(--sf-az);font-size:9px;letter-spacing:2.5px;text-transform:uppercase;transition:color .5s,border-color .5s;}
    .sf-hero-label-dot{width:5px;height:5px;border-radius:50%;flex-shrink:0;transition:background .5s;}
    .sf-hero-counter{font-family:var(--sf-az);font-size:10px;letter-spacing:2px;color:rgba(255,255,255,.26);}
    .sf-hero-body{position:relative;z-index:10;padding:0 clamp(16px,5vw,60px) clamp(32px,5vh,52px);display:flex;align-items:flex-end;justify-content:space-between;gap:24px;}
    .sf-hero-left{flex:1;max-width:500px;}
    .sf-hero-sub{font-family:var(--sf-az);font-size:9px;letter-spacing:3px;text-transform:uppercase;margin-bottom:10px;display:flex;align-items:center;gap:10px;transition:color .5s;}
    .sf-hero-sub-line{display:block;height:1.5px;width:22px;border-radius:1px;flex-shrink:0;transition:background .5s;}
    .sf-hero-title{font-family:var(--sf-az);font-size:clamp(24px,4.5vw,56px);line-height:1.04;letter-spacing:-.5px;color:#fff;margin-bottom:10px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis;}
    .sf-hero-title-east{font-family:var(--sf-jp)!important;}
    .sf-hero-title-west{font-family:var(--sf-wt)!important;}
    .sf-hero-meta{display:flex;gap:8px;align-items:center;margin-bottom:12px;flex-wrap:wrap;}
    .sf-hero-meta-badge{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:100px;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.1);font-size:10px;color:rgba(255,255,255,.6);font-family:var(--sf-ui);white-space:nowrap;}
    .sf-hero-desc{font-family:var(--sf-bd);font-size:clamp(13px,1.5vw,15px);color:rgba(255,255,255,.44);line-height:1.72;margin-bottom:24px;max-width:400px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;text-overflow:ellipsis;}
    .sf-hero-btns{display:flex;gap:8px;flex-wrap:wrap;align-items:center;}
 
    /* GLOW BUTTONS */
    @keyframes sfGlowUp{0%{box-shadow:0 0 0 transparent;transform:translateY(0);}55%{box-shadow:0 0 28px var(--btn-glow,rgba(255,255,255,.3)),0 8px 22px var(--btn-glow,rgba(255,255,255,.18));transform:translateY(-3px);}100%{box-shadow:0 0 16px var(--btn-glow,rgba(255,255,255,.2)),0 5px 14px var(--btn-glow,rgba(255,255,255,.14));transform:translateY(-2px);}}
    @keyframes sfGlowDown{from{box-shadow:0 0 16px var(--btn-glow,rgba(255,255,255,.2));transform:translateY(-2px);}to{box-shadow:0 0 0 transparent;transform:translateY(0);}}
    .sf-btn{display:inline-flex;align-items:center;gap:7px;padding:11px 20px;border-radius:9px;font-family:var(--sf-az);font-size:10px;letter-spacing:1.5px;text-transform:uppercase;text-decoration:none;cursor:pointer;border:none;outline:none;white-space:nowrap;animation:sfGlowDown .4s ease forwards;transition:background .25s,border-color .25s;}
    .sf-btn:hover{animation:sfGlowUp .35s ease forwards;}
    .sf-btn-play{background:#fff;color:#07070b!important;font-weight:700;--btn-glow:rgba(255,255,255,.55);}
    .sf-btn-play:hover{background:rgba(255,255,255,.9);}
    .sf-btn-outline{background:rgba(0,0,0,.3);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);border:1px solid;color:inherit;}
    .sf-btn-more{background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.14);color:rgba(255,255,255,.7);--btn-glow:rgba(255,255,255,.25);}
 
    /* THUMBNAILS */
    .sf-hero-thumbs{display:flex;gap:8px;align-items:flex-end;flex-shrink:0;}
    .sf-thumb{position:relative;border-radius:10px;overflow:hidden;cursor:pointer;transition:all .38s cubic-bezier(.4,0,.2,1);border:1px solid rgba(255,255,255,.07);flex-shrink:0;width:62px;height:92px;}
    .sf-thumb-active{width:134px!important;height:192px!important;border-color:rgba(255,255,255,.24);box-shadow:0 16px 48px rgba(0,0,0,.7);}
    .sf-thumb img{width:100%;height:100%;object-fit:cover;transition:transform .55s;filter:brightness(.7);}
    .sf-thumb-active img{filter:brightness(.9);}
    .sf-thumb:hover img{transform:scale(1.06);}
    .sf-thumb-label{position:absolute;bottom:0;left:0;right:0;padding:20px 7px 6px;background:linear-gradient(to top,rgba(0,0,0,.85),transparent);font-family:var(--sf-az);font-size:8px;letter-spacing:2px;text-align:center;opacity:0;transition:opacity .3s;}
    .sf-thumb-active .sf-thumb-label{opacity:1;}
    .sf-thumb-top-line{position:absolute;top:0;left:0;right:0;height:2px;border-radius:1px 1px 0 0;transition:background .5s;}
 
    /* DOTS */
    .sf-dots{position:absolute;bottom:clamp(8px,1.5vh,16px);left:50%;transform:translateX(-50%);z-index:10;display:flex;gap:6px;align-items:center;}
    .sf-dot{height:3px;border-radius:2px;background:rgba(255,255,255,.26);cursor:pointer;transition:all .35s ease;}
    .sf-dot-active{background:#fff;}
 
    /* HERO RESPONSIVE */
    @media(max-width:768px){
      .sf-hero-bg-wrap{inset:-20px;}
      .sf-hero-bg-desk{display:none;}
      .sf-hero-bg-mob{display:block;}
      .sf-hero-radial{background:radial-gradient(ellipse 80% 60% at 50% 35%,transparent 0%,transparent 20%,rgba(7,7,11,0.5) 55%,rgba(7,7,11,0.95) 100%);}
      .sf-hero-edge{box-shadow:inset 0 60px 60px rgba(7,7,11,0.5),inset 0 -10px 40px rgba(7,7,11,0.1);}
      .sf-hero-thumbs{display:none;}
      .sf-hero-body{flex-direction:column;align-items:flex-start;padding:0 16px clamp(52px,8vh,72px);gap:0;}
      .sf-hero-left{max-width:100%;}
      .sf-hero-title{font-size:clamp(22px,8vw,36px);}
      .sf-hero-desc{font-size:13px;max-width:100%;}
      .sf-btn{padding:10px 14px;font-size:9px;}
    }
    @media(max-width:480px){
      .sf-hero{min-height:480px;max-height:700px;}
      .sf-hero-title{font-size:clamp(20px,9vw,30px);}
      .sf-hero-btns{width:100%;}
      .sf-btn{flex:1;justify-content:center;min-width:0;}
    }
 
    /* TOGGLE */
    .sf-toggle-section{padding:clamp(24px,4vh,40px) 0 0;position:relative;z-index:1;}
    .sf-toggle-wrap{display:flex;align-items:center;justify-content:center;gap:4px;padding:4px;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.08);border-radius:14px;width:fit-content;margin:0 auto;}
    .sf-toggle-btn{display:inline-flex;align-items:center;gap:8px;padding:10px 24px;border-radius:10px;border:none;cursor:pointer;font-family:var(--sf-az);font-size:10px;letter-spacing:2px;text-transform:uppercase;transition:all .3s ease;background:transparent;color:rgba(255,255,255,.4);white-space:nowrap;}
    .sf-toggle-btn-active-east{background:var(--sf-east);color:#fff!important;box-shadow:0 4px 20px rgba(220,38,38,.4);}
    .sf-toggle-btn-active-west{background:var(--sf-west);color:#07070b!important;box-shadow:0 4px 20px rgba(6,182,212,.4);}
    @media(max-width:480px){
      .sf-toggle-wrap{gap:2px;padding:3px;width:100%;max-width:320px;}
      .sf-toggle-btn{padding:9px 12px;font-size:9px;gap:4px;flex:1;justify-content:center;}
      .sf-toggle-btn svg{display:none;}
    }
 
    /* SECTION HEADER */
    .sf-sh{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;gap:12px;}
    .sf-sh-left{display:flex;align-items:center;gap:10px;}
    .sf-sh-bar{width:3px;height:24px;border-radius:2px;flex-shrink:0;}
    .sf-sh-label{font-family:var(--sf-az);font-size:12px;letter-spacing:2px;text-transform:uppercase;}
 
    /* CAROUSEL */
    .sf-carousel-root{position:relative;overflow:hidden;}
    .sf-carousel-track{display:flex;gap:10px;overflow-x:scroll;padding-bottom:6px;scrollbar-width:none;-webkit-overflow-scrolling:touch;}
    .sf-carousel-track::-webkit-scrollbar{display:none;}
    .sf-carousel-fade-l{pointer-events:none;position:absolute;inset-y:0;left:0;width:48px;background:linear-gradient(to right,var(--sf-bg),transparent);z-index:2;}
    .sf-carousel-fade-r{pointer-events:none;position:absolute;inset-y:0;right:0;width:48px;background:linear-gradient(to left,var(--sf-bg),transparent);z-index:2;}
 
    /* POSTER */
    .sf-poster{position:relative;flex-shrink:0;width:clamp(128px,16vw,192px);cursor:pointer;}
    .sf-poster-inner{position:relative;aspect-ratio:2/3;overflow:hidden;border-radius:11px;border:1px solid rgba(255,255,255,.07);background:rgba(255,255,255,.04);transition:all .38s cubic-bezier(.4,0,.2,1);}
    .sf-poster:hover .sf-poster-inner{transform:scale(1.05);border-color:rgba(255,255,255,.18);box-shadow:0 20px 54px rgba(0,0,0,.55);}
    .sf-poster-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;}
    .sf-poster-grad{position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,.85) 0%,transparent 55%);}
    .sf-poster-play{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .3s;}
    .sf-poster:hover .sf-poster-play{opacity:1;}
    .sf-poster-play-btn{width:42px;height:42px;border-radius:50%;background:rgba(255,255,255,.92);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 20px rgba(0,0,0,.5);}
    .sf-poster-rating{position:absolute;top:8px;right:8px;display:flex;align-items:center;gap:3px;background:rgba(0,0,0,.65);border-radius:100px;padding:3px 7px;font-size:9px;font-family:var(--sf-ui);}
    .sf-poster-body{padding:7px 2px 0;}
    .sf-poster-title{font-size:11px;font-weight:600;color:#fff;line-height:1.35;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;font-family:var(--sf-ui);}
    .sf-poster-meta{display:flex;align-items:center;gap:5px;margin-top:3px;font-size:9px;color:rgba(255,255,255,.35);font-family:var(--sf-ui);}
 
    /* SKELETON */
    .sf-skeleton{background:rgba(255,255,255,.07);border-radius:12px;animation:sfPulse 1.8s ease-in-out infinite;}
    @keyframes sfPulse{0%,100%{opacity:.6;}50%{opacity:.3;}}
 
    /* REC GRID — expanded to fill screen width */
    .sf-rec-grid{display:grid;gap:12px;grid-template-columns:repeat(8,1fr);}
    .sf-rec-grid-full{display:grid;gap:clamp(8px,1vw,14px);grid-template-columns:repeat(auto-fill,minmax(clamp(120px,14vw,220px),1fr));width:100%;}
    .sf-rec-section{background:var(--sf-bg);overflow:hidden;}
    @media(max-width:1400px){
      .sf-rec-grid{grid-template-columns:repeat(7,1fr);}
      .sf-rec-grid-full{grid-template-columns:repeat(auto-fill,minmax(clamp(110px,13vw,200px),1fr));}
    }
    @media(max-width:1200px){.sf-rec-grid{grid-template-columns:repeat(6,1fr);}}
    @media(max-width:1100px){.sf-rec-grid{grid-template-columns:repeat(5,1fr);}
      .sf-rec-grid-full{gap:clamp(6px,0.8vw,12px);}
    }
    @media(max-width:860px){.sf-rec-grid{grid-template-columns:repeat(4,1fr);gap:10px;}
      .sf-rec-grid-full{grid-template-columns:repeat(auto-fill,minmax(clamp(100px,16vw,180px),1fr));gap:8px;}
    }
    @media(max-width:640px){.sf-rec-grid{grid-template-columns:repeat(3,1fr);gap:8px;}
      .sf-rec-grid-full{grid-template-columns:repeat(4,1fr);gap:6px;}
      .sf-rec-section{padding-left:16px!important;padding-right:16px!important;margin-left:0!important;margin-right:0!important;}
    }
    @media(max-width:450px){.sf-rec-grid{grid-template-columns:repeat(2,1fr);gap:8px;}
      .sf-rec-grid-full{grid-template-columns:repeat(2,1fr);gap:6px;}
    }
 
    /* PORTAL CARD */
    .sf-portal-card{position:relative;border-radius:18px;overflow:hidden;aspect-ratio:21/9;min-height:240px;max-height:400px;cursor:pointer;transition:transform .35s ease;}
    .sf-portal-card:hover{transform:scale(1.015);}
    .sf-portal-card-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:center;}
    .sf-portal-card-grad{position:absolute;inset:0;background:linear-gradient(to right,rgba(7,7,11,.9) 0%,rgba(7,7,11,.6) 38%,rgba(7,7,11,.28) 100%);}
    .sf-portal-card-content{position:relative;z-index:2;height:100%;display:flex;flex-direction:column;align-items:flex-start;justify-content:center;padding:clamp(20px,4vw,48px);}
    .sf-portal-card-label{font-family:var(--sf-az);font-size:9px;letter-spacing:3px;color:rgba(255,255,255,.45);text-transform:uppercase;margin-bottom:10px;}
    .sf-portal-card-title{font-family:var(--sf-az);font-size:clamp(22px,3.5vw,38px);letter-spacing:.5px;margin-bottom:18px;max-width:380px;}
    .sf-portal-card-btn{display:inline-flex;align-items:center;gap:8px;padding:11px 24px;border-radius:10px;background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.15);color:#fff;font-family:var(--sf-az);font-size:10px;letter-spacing:1.5px;text-transform:uppercase;backdrop-filter:blur(10px);cursor:pointer;transition:all .25s;}
    .sf-portal-card:hover .sf-portal-card-btn{background:rgba(255,255,255,.18);border-color:rgba(255,255,255,.28);}
    @media(max-width:640px){
      .sf-portal-card{aspect-ratio:4/3;min-height:220px;max-height:320px;}
      .sf-portal-card-grad{background:linear-gradient(to top,rgba(7,7,11,.95) 0%,rgba(7,7,11,.55) 50%,rgba(7,7,11,.18) 100%);}
      .sf-portal-card-content{align-items:center;text-align:center;justify-content:flex-end;padding-bottom:clamp(20px,4vh,36px);}
      .sf-portal-card-title{max-width:100%;font-size:clamp(18px,6vw,26px);}
    }
 
    /* TRAILER MODAL */
    .sf-modal-backdrop{position:fixed;inset:0;z-index:200;background:rgba(0,0,0,.82);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);display:flex;align-items:center;justify-content:center;padding:clamp(12px,3vw,24px);}
    .sf-modal-box{position:relative;width:100%;max-width:900px;aspect-ratio:16/9;border-radius:16px;overflow:hidden;background:#000;box-shadow:0 32px 100px rgba(0,0,0,.8);}
    .sf-modal-close{position:absolute;top:12px;right:12px;z-index:10;width:34px;height:34px;border-radius:50%;background:rgba(0,0,0,.68);border:1px solid rgba(255,255,255,.15);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;}
    .sf-modal-close:hover{background:rgba(255,255,255,.15);}
    .sf-modal-no-trailer{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;text-align:center;padding:24px;}
 
    /* INFO MODAL */
    .sf-info-backdrop{position:fixed;inset:0;z-index:200;background:rgba(0,0,0,.88);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);display:flex;align-items:center;justify-content:center;padding:clamp(12px,3vw,24px);}
    .sf-info-box{position:relative;width:100%;max-width:460px;border-radius:18px;overflow:hidden;background:linear-gradient(145deg,rgba(20,20,25,.97),rgba(10,10,14,.99));box-shadow:0 32px 100px rgba(0,0,0,.8),0 0 40px rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08);}
    .sf-info-img{width:100%;height:200px;object-fit:cover;display:block;}
    .sf-info-body{padding:20px;}
    .sf-info-title{font-family:var(--sf-az);font-size:17px;letter-spacing:.5px;margin-bottom:10px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
    .sf-info-meta{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:14px;}
    .sf-info-badge{display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:100px;background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.1);font-size:10px;color:rgba(255,255,255,.6);}
    .sf-info-desc{font-family:var(--sf-bd);font-size:12px;color:rgba(255,255,255,.52);line-height:1.6;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;}
    .sf-info-close{position:absolute;top:11px;right:11px;z-index:10;width:30px;height:30px;border-radius:50%;background:rgba(0,0,0,.58);border:1px solid rgba(255,255,255,.12);display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;}
    .sf-info-close:hover{background:rgba(255,255,255,.15);}
 
    /* WATCHLIST PANEL */
    .sf-wl-backdrop{position:fixed;inset:0;z-index:200;background:rgba(0,0,0,.88);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);display:flex;align-items:center;justify-content:center;padding:clamp(12px,3vw,24px);}
    .sf-wl-box{position:relative;width:100%;max-width:380px;border-radius:18px;background:linear-gradient(145deg,rgba(20,20,25,.97),rgba(10,10,14,.99));box-shadow:0 32px 100px rgba(0,0,0,.8),0 0 60px rgba(6,182,212,.1);border:1px solid rgba(6,182,212,.2);text-align:center;padding:clamp(28px,5vw,44px) clamp(20px,4vw,32px);}
    .sf-wl-icon{width:60px;height:60px;margin:0 auto 18px;border-radius:50%;background:rgba(6,182,212,.15);display:flex;align-items:center;justify-content:center;}
    .sf-wl-title{font-family:var(--sf-az);font-size:15px;letter-spacing:1px;color:#06b6d4;margin-bottom:10px;}
    .sf-wl-msg{font-size:13px;color:rgba(255,255,255,.48);line-height:1.6;margin-bottom:22px;}
    .sf-wl-btn{display:inline-flex;align-items:center;gap:7px;padding:10px 22px;border-radius:10px;background:#06b6d4;color:#07070b;font-family:var(--sf-az);font-size:10px;letter-spacing:1.5px;text-transform:uppercase;border:none;cursor:pointer;transition:all .25s;}
    .sf-wl-btn:hover{background:#22d3ee;}
    .sf-wl-close{position:absolute;top:12px;right:12px;width:26px;height:26px;border-radius:50%;background:rgba(255,255,255,.07);border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;}
 
    /* NOTICE */
    .sf-notice{display:flex;align-items:center;gap:10px;padding:11px 16px;border-radius:11px;background:rgba(234,179,8,.08);border:1px solid rgba(234,179,8,.2);font-size:12px;color:rgba(255,255,255,.58);font-family:var(--sf-ui);margin-bottom:20px;flex-wrap:wrap;}
 
    /* SPACING */
    .sf-div{height:1px;background:linear-gradient(to right,transparent,rgba(255,255,255,.06),transparent);margin:clamp(32px,5vh,56px) 0;}
    .sf-sec{padding:clamp(32px,5vh,56px) 0;}
    .sf-sec-sm{padding:clamp(20px,3vh,36px) 0;}
 
    /* FOOTER LINKS */
    .sf-footer-links{display:flex;justify-content:center;gap:10px;flex-wrap:wrap;margin-bottom:18px;}
    .sf-footer-link{display:inline-flex;align-items:center;gap:6px;padding:8px 18px;border-radius:9px;text-decoration:none;font-family:var(--sf-az);font-size:10px;letter-spacing:1.5px;text-transform:uppercase;transition:all .25s;cursor:pointer;background:none;}
    .sf-fl-east{border:1px solid rgba(220,38,38,.3);color:#dc2626;}
    .sf-fl-east:hover{border-color:rgba(220,38,38,.55);background:rgba(220,38,38,.07);}
    .sf-fl-west{border:1px solid rgba(6,182,212,.3);color:#06b6d4;}
    .sf-fl-west:hover{border-color:rgba(6,182,212,.55);background:rgba(6,182,212,.07);}
    .sf-fl-refresh{border:1px solid rgba(255,255,255,.09);color:rgba(255,255,255,.38);}
    .sf-fl-refresh:hover{background:rgba(255,255,255,.09);color:rgba(255,255,255,.6);}
    @media(max-width:480px){.sf-footer-link{padding:8px 12px;font-size:9px;}}
  `}</style>
);

/* ═══════════════════════════════════════════════════════════════════
   UTILITIES
═══════════════════════════════════════════════════════════════════ */
const cx = (...c) => c.filter(Boolean).join(' ');
const rand = (mn, mx) => Math.floor(Math.random() * (mx - mn + 1)) + mn;
const sample = (a) => a[rand(0, a.length - 1)];
const shuffle = (a) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[b[i], b[j]] = [b[j], b[i]]; } return b; };
const dedupeBy = (items, k = 'id') => { const m = new Map(); for (const it of items) if (!m.has(it[k])) m.set(it[k], it); return [...m.values()]; };
const mergeDedup = (lists, k = 'id') => dedupeBy(lists.flat().filter(Boolean), k);

function pLimit(n) {
  let active = 0; const q = [];
  const next = () => { active--; if (q.length) q.shift()(); };
  const run = (fn, res, ...args) => { active++; Promise.resolve(fn(...args)).then(res).then(next, next); };
  return (fn, ...args) => new Promise(res => { const t = run.bind(null, fn, res, ...args); active < n ? t() : q.push(t); });
}

const FALLBACK_POSTERS = [
  'https://picsum.photos/342/513?random=11',
  'https://picsum.photos/342/513?random=22',
  'https://picsum.photos/342/513?random=33',
  'https://picsum.photos/342/513?random=44',
];
const FALLBACK_BDROPS = [
  'https://picsum.photos/1280/720?random=55',
  'https://picsum.photos/1280/720?random=66',
];
const rp = () => sample(FALLBACK_POSTERS);

/* ═══════════════════════════════════════════════════════════════════
   API HELPERS
═══════════════════════════════════════════════════════════════════ */
const TMDB_IMG = 'https://image.tmdb.org/t/p';
const TMDB_KEY = process.env.REACT_APP_TMDB_API_KEY;
const TRAKT_KEY = process.env.REACT_APP_TRAKT_KEY;

// API keys are used by tmdbRequest and traktRequest utilities
// eslint-disable-next-line no-unused-vars
const _apiCheck = (() => ({ hasTMDB: !!TMDB_KEY, hasTRAKT: !!TRAKT_KEY }))();

// Create a context for Streamfinite data
const StreamfiniteContext = createContext(null);

export const useStreamfiniteContext = () => {
  const context = useContext(StreamfiniteContext);
  if (!context) {
    throw new Error('useStreamfiniteContext must be used within a StreamfiniteProvider');
  }
  return context;
};

const GQL_TREND = `query($p:Int,$n:Int){Page(page:$p,perPage:$n){media(type:ANIME,sort:TRENDING_DESC){id title{romaji english}averageScore startDate{year}format description(asHtml:false)coverImage{extraLarge large}bannerImage trailer{id site}}}}`;
const GQL_TOP   = `query($p:Int,$n:Int){Page(page:$p,perPage:$n){media(type:ANIME,sort:SCORE_DESC){id title{romaji english}averageScore startDate{year}format description(asHtml:false)coverImage{extraLarge large}bannerImage trailer{id site}}}}`;
const GQL_UP    = `query($p:Int,$n:Int){Page(page:$p,perPage:$n){media(type:ANIME,sort:START_DATE,status_not_in:[FINISHED,CANCELLED]){id title{romaji english}averageScore startDate{year}format description(asHtml:false)coverImage{extraLarge large}bannerImage trailer{id site}}}}`;

const mapAL = (m) => ({
  id: `east-al-${m.id}`, title: m.title?.english || m.title?.romaji || 'Untitled',
  year: m.startDate?.year || '—', rating: m.averageScore ? +(m.averageScore / 10).toFixed(1) : 0,
  poster: m.coverImage?.extraLarge || m.coverImage?.large || rp(),
  backdrop: m.bannerImage || sample(FALLBACK_BDROPS), type: 'anime', region: 'east',
  synopsis: m.description || '',
  trailerUrl: m.trailer?.site?.toLowerCase() === 'youtube' && m.trailer?.id ? `https://www.youtube.com/watch?v=${m.trailer.id}` : null,
  _meta: { source: 'anilist', id: m.id }
});

const mapJikan = (a) => ({
  id: `east-jk-${a.mal_id}`, title: a.title_english || a.title || 'Untitled',
  year: a.year || a.aired?.prop?.from?.year || '—',
  rating: a.score ? +a.score.toFixed(1) : 0,
  poster: a.images?.jpg?.large_image_url || a.images?.jpg?.image_url || rp(),
  backdrop: a.trailer?.images?.maximum_image_url || sample(FALLBACK_BDROPS),
  type: 'anime', region: 'east', synopsis: a.synopsis || '',
  trailerUrl: a.trailer?.youtube_id ? `https://www.youtube.com/watch?v=${a.trailer.youtube_id}` : null,
  _meta: { source: 'jikan', id: a.mal_id }
});

const tmdbPoster = (p, s = 'w342') => p ? `${TMDB_IMG}/${s}${p}` : rp();
const tmdbBdrop  = (p, s = 'w1280') => p ? `${TMDB_IMG}/${s}${p}` : sample(FALLBACK_BDROPS);
const mapTMDB = (r) => {
  const isM = r.media_type ? r.media_type === 'movie' : !!r.title;
  return {
    id: `west-tmdb-${isM ? 'mv' : 'tv'}-${r.id}`,
    title: r.title || r.name || 'Untitled',
    year: (r.release_date || r.first_air_date || '').slice(0, 4) || '—',
    rating: r.vote_average ? +r.vote_average.toFixed(1) : 0,
    poster: tmdbPoster(r.poster_path), backdrop: tmdbBdrop(r.backdrop_path),
    type: isM ? 'movie' : 'tv', region: 'west', synopsis: r.overview || '',
    // Also include trailer from TMDB if available
    trailerUrl: r.title ? `https://www.youtube.com/results?search_query=${encodeURIComponent(r.title + ' ' + (isM ? 'movie' : 'tv') + ' trailer')}` : null,
    _meta: { source: 'tmdb', tmdbId: r.id, tmdbType: isM ? 'movie' : 'tv' }
  };
};

const mapTrakt = (item, type) => {
  const d = item[type] || item;
  return {
    id: `west-trakt-${type}-${d?.ids?.trakt}`, title: d?.title || 'Untitled',
    year: d?.year || '—', rating: d?.rating ? +d.rating.toFixed(1) : 0,
    poster: rp(), backdrop: sample(FALLBACK_BDROPS),
    type: type === 'movie' ? 'movie' : 'tv', region: 'west',
    trailerUrl: d?.trailer || null, synopsis: '',
    _meta: { source: 'trakt', traktId: d?.ids?.trakt, tmdbId: d?.ids?.tmdb, tmdbType: type }
  };
};

async function fetchTMDBPath(path) {
  return tmdbRequest(path);
}
async function tmdbTrailer(id, type) {
  try {
    const j = await fetchTMDBPath(`/${type}/${id}/videos`);
    const r = j?.results || [];
    // Look for YouTube trailer or teaser
    const v = r.find(x => x.site === 'YouTube' && /Trailer|Teaser/i.test(x.type)) || r.find(x => x.site === 'YouTube');
    return v ? `https://www.youtube.com/watch?v=${v.key}` : null;
  } catch (e) {
    console.error('Error fetching trailer:', e);
    return null;
  }
}

// Fetch trailers for TMDB items after initial fetch
async function enrichTMDBWithTrailers(items) {
  const lim = pLimit(5);
  return Promise.all(items.map(it => lim(async () => {
    const { tmdbId, tmdbType = 'movie' } = it._meta || {};
    if (!tmdbId) return it;
    try {
      // Only fetch if no trailer URL exists
      if (!it.trailerUrl) {
        const trailer = await tmdbTrailer(tmdbId, tmdbType);
        if (trailer) {
          return { ...it, trailerUrl: trailer };
        }
      }
      return it;
    } catch { return it; }
  })));
}

async function enrichTrakt(items) {
  const lim = pLimit(5);
  return Promise.all(items.map(it => lim(async () => {
    const { tmdbId, tmdbType = 'movie' } = it._meta || {};
    if (!tmdbId) return it;
    try {
      const d = await fetchTMDBPath(`/${tmdbType}/${tmdbId}`);
      const trailer = it.trailerUrl || await tmdbTrailer(tmdbId, tmdbType);
      return { ...it, poster: tmdbPoster(d.poster_path), backdrop: tmdbBdrop(d.backdrop_path), trailerUrl: trailer, synopsis: d.overview || it.synopsis, year: it.year || (d.release_date || d.first_air_date || '').slice(0, 4) };
    } catch { return it; }
  })));
}

/* ═══════════════════════════════════════════════════════════════════
   DATA HOOK
   ═══════════════════════════════════════════════════════════════════ */

// Simple in-memory cache for the main Streamfinite data (per mode)
let streamfiniteDataCache = {};
const DATA_CACHE_TTL = 3 * 60 * 1000; // 3 minutes

function useStreamData(mode) {
  const [state, setState] = useState({ trending: [], upcoming: [], topRated: [], recommended: [], loading: true, error: null });

  const load = useCallback(async (force = false) => {
    const cacheKey = `streamfinite-${mode}`;

    // Serve from cache if fresh (huge win for repeated toggles)
    const cached = streamfiniteDataCache[cacheKey];
    if (!force && cached && Date.now() - cached.timestamp < DATA_CACHE_TTL) {
      setState({ ...cached.data, loading: false, error: null });
      return;
    }

    setState(s => ({ ...s, loading: true, error: null }));

    try {
      let trending = [], upcoming = [], topRated = [];

      if (mode === 'east') {
        const [alTr, jkTr, alUp, jkUp, alTop, jkTop] = await Promise.allSettled([
          anilistQuery(GQL_TREND, { p: 1, n: 20 }).then(d => (d?.Page?.media || []).map(mapAL)),
          jikanRequest('top/anime?limit=20').then(d => (d?.data || []).map(mapJikan)),
          anilistQuery(GQL_UP, { p: 1, n: 20 }).then(d => (d?.Page?.media || []).map(mapAL)),
          jikanRequest('seasons/upcoming?limit=20').then(d => (d?.data || []).map(mapJikan)),
          anilistQuery(GQL_TOP, { p: 1, n: 20 }).then(d => (d?.Page?.media || []).map(mapAL)),
          jikanRequest('top/anime?filter=bypopularity&limit=20').then(d => (d?.data || []).map(mapJikan)),
        ]);
        trending  = mergeDedup([alTr.value  || [], jkTr.value  || []]);
        upcoming  = mergeDedup([alUp.value  || [], jkUp.value  || []]);
        topRated  = mergeDedup([alTop.value || [], jkTop.value || []]);
      } else {
        // West - fetch base data first (fast path)
        const [tmTr, tmUp, tmTop, tkTr, tkPop] = await Promise.allSettled([
          fetchTMDBPath('/trending/all/day').then(d => (d?.results || []).map(mapTMDB)),
          Promise.all([fetchTMDBPath('/movie/upcoming'), fetchTMDBPath('/tv/on_the_air')])
            .then(([m, t]) => mergeDedup([(m?.results || []).map(r => mapTMDB({ ...r, media_type: 'movie' })), (t?.results || []).map(r => mapTMDB({ ...r, media_type: 'tv' }))])),
          Promise.all([fetchTMDBPath('/movie/top_rated'), fetchTMDBPath('/tv/top_rated')])
            .then(([m, t]) => mergeDedup([(m?.results || []).map(r => mapTMDB({ ...r, media_type: 'movie' })), (t?.results || []).map(r => mapTMDB({ ...r, media_type: 'tv' }))])),
          traktRequest('/movies/trending?page=1&limit=20&extended=full').then(d => (d || []).map(i => mapTrakt(i, 'movie'))),
          traktRequest('/shows/popular?page=1&limit=20').then(d => (d || []).map(i => mapTrakt(i, 'show'))),
        ]);

        // Set basic data immediately so the UI can render (big perceived speed win)
        const basicTrending = mergeDedup([tmTr.value || [], tkTr.value || []]);
        const basicUpcoming = tmUp.value || [];
        const basicTopRated = mergeDedup([tmTop.value || [], tkPop.value || []]);

        setState({
          trending: basicTrending,
          upcoming: basicUpcoming,
          topRated: basicTopRated,
          recommended: shuffle(mergeDedup([basicTrending, basicUpcoming, basicTopRated])).slice(0, 24),
          loading: true, // still loading trailers
          error: null,
        });

        // Enrich trailers in the background (non-blocking)
        const [tkTrE, tkPopE, tmTrWithTrailers, tmUpWithTrailers, tmTopWithTrailers] = await Promise.all([
          enrichTrakt(tkTr.value || []),
          enrichTrakt(tkPop.value || []),
          enrichTMDBWithTrailers(tmTr.value || []),
          enrichTMDBWithTrailers(tmUp.value || []),
          enrichTMDBWithTrailers(tmTop.value || []),
        ]);

        trending = mergeDedup([tmTrWithTrailers, tkTrE]);
        upcoming = tmUpWithTrailers;
        topRated = mergeDedup([tmTopWithTrailers, tkPopE]);
      }

      const pool = mergeDedup([trending, upcoming, topRated]);
      const finalState = {
        trending,
        upcoming,
        topRated,
        recommended: shuffle(pool).slice(0, 24),
        loading: false,
        error: null,
      };

      // Cache the final result
      streamfiniteDataCache[`streamfinite-${mode}`] = {
        data: finalState,
        timestamp: Date.now(),
      };

      setState(finalState);
    } catch (e) {
      setState(s => ({ ...s, loading: false, error: e }));
    }
  }, [mode]);

  useEffect(() => { load(); }, [load]);
  return { ...state, refresh: load };
}

/* ═══════════════════════════════════════════════════════════════════
   TRAILER MODAL
═══════════════════════════════════════════════════════════════════ */
function toEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com')) { const v = u.searchParams.get('v'); return v ? `https://www.youtube.com/embed/${v}?autoplay=1` : null; }
    if (u.hostname.includes('youtu.be')) return `https://www.youtube.com/embed/${u.pathname.replace('/', '')}?autoplay=1`;
    if (u.hostname.includes('vimeo.com')) { const v = u.pathname.split('/').filter(Boolean)[0]; return v ? `https://player.vimeo.com/video/${v}?autoplay=1` : null; }
    return url;
  } catch { return null; }
}

const TrailerModal = ({ open, onClose, title, trailerUrl }) => {
  const embed = toEmbed(trailerUrl);
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div className="sf-modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
          <motion.div className="sf-modal-box"
            initial={{ scale: .9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: .95, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 220, damping: 22 }}>
            {embed
              ? <iframe title={title || 'Trailer'} src={embed} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              : <div className="sf-modal-no-trailer">
                  <Play size={32} color="rgba(255,255,255,.3)" />
                  <p style={{ fontFamily: 'var(--sf-az)', fontSize: '14px', color: 'rgba(255,255,255,.5)', letterSpacing: '2px' }}>Trailer Not Available</p>
                  <p style={{ fontSize: '12px', color: 'rgba(255,255,255,.28)', fontFamily: 'var(--sf-ui)' }}>Try another title or switch regions.</p>
                </div>}
            <button className="sf-modal-close" onClick={onClose} aria-label="Close">
              <X size={16} color="rgba(255,255,255,.8)" />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

/* ═══════════════════════════════════════════════════════════════════
   HERO SECTION (Streamfinite-specific, slides from live data)
═══════════════════════════════════════════════════════════════════ */
const SFHero = ({ items, mode, onPlay, onInfo, onWatchlist }) => {
  const [active, setActive] = useState(0);
  const [fading, setFading] = useState(false);
  const timerRef = useRef(null);

  const slides = useMemo(() => {
    if (!items?.length) return [];
    return items.slice(0, 5);
  }, [items]);

  useEffect(() => {
    if (!slides.length) return;
    timerRef.current = setInterval(() => {
      setFading(true);
      setTimeout(() => { setActive(p => (p + 1) % slides.length); setFading(false); }, 360);
    }, 6000);
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  const switchTo = (i) => {
    if (i === active || fading || !slides.length) return;
    clearInterval(timerRef.current);
    setFading(true);
    setTimeout(() => { setActive(i); setFading(false); }, 320);
  };

  if (!slides.length) return (
    <div className="sf-hero" style={{ alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div className="sf-skeleton" style={{ width: '280px', height: '32px', margin: '0 auto 16px' }} />
        <div className="sf-skeleton" style={{ width: '180px', height: '18px', margin: '0 auto' }} />
      </div>
    </div>
  );

  const s = slides[active];
  const isEast = mode === 'east';
  const accent = isEast ? '#dc2626' : '#06b6d4';
  const glow   = isEast ? 'rgba(220,38,38,.55)' : 'rgba(6,182,212,.55)';
  const regionLabel = isEast ? 'ANIME' : 'FILM';

  return (
    <div className="sf-hero">
      {/* BG */}
      <div className="sf-hero-bg-wrap">
        <AnimatePresence mode="sync">
          <motion.img key={active} src={s.poster} alt="" className="sf-hero-bg-desk"
            initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1.04 }}
            exit={{ opacity: 0 }} transition={{ duration: .75 }} />
          <motion.img key={`mobile-${active}`} src={s.poster} alt="" className="sf-hero-bg-mob"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            exit={{ opacity: 0 }} transition={{ duration: .75 }} style={{ width: '100%', height: '100%' }} />
        </AnimatePresence>
        <div className="sf-hero-radial" />
        <div className="sf-hero-edge" />
        <div className="sf-hero-bottom" />
        <div className="sf-hero-tint" style={{ background: `radial-gradient(ellipse 48% 48% at 65% 34%,${accent}12 0%,transparent 58%)` }} />
      </div>

      {/* Top bar */}
      <div className="sf-hero-topbar">
        <div className="sf-hero-label" style={{ color: accent, borderColor: `${accent}40` }}>
          <span className="sf-hero-label-dot" style={{ background: accent }} />
          Streamfinite · {regionLabel}
        </div>
        <span className="sf-hero-counter">
          {String(active + 1).padStart(2, '0')}&nbsp;/&nbsp;{String(slides.length).padStart(2, '0')}
        </span>
      </div>

      {/* Body */}
      <div className="sf-hero-body">
        <div className="sf-hero-left">
          <motion.div key={`sub-${active}`} className="sf-hero-sub" style={{ color: accent }}
            initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: .45 }}>
            <span className="sf-hero-sub-line" style={{ background: accent }} />
            {s.type?.toUpperCase()} · {s.year}
          </motion.div>

          <motion.h1
            key={`title-${active}`}
            className={`sf-hero-title${isEast ? ' sf-hero-title-east' : ' sf-hero-title-west'}`}
            style={{ opacity: fading ? 0 : 1, transition: 'opacity .32s' }}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .45, delay: .07 }}>
            {s.title}
          </motion.h1>

          <div className="sf-hero-meta">
            {s.rating > 0 && (
              <span className="sf-hero-meta-badge"><Star size={10} />{s.rating}</span>
            )}
            <span className="sf-hero-meta-badge">{s.type === 'anime' ? <Tv size={10} /> : <Film size={10} />}{s.type}</span>
            {s.trailerUrl && <span className="sf-hero-meta-badge" style={{ color: accent, borderColor: `${accent}40` }}><Play size={10} />Trailer Available</span>}
          </div>

          <motion.p key={`desc-${active}`} className="sf-hero-desc"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: fading ? 0 : 1, y: 0 }} transition={{ duration: .42, delay: .1 }}>
            {s.synopsis?.replace(/<[^>]*>/g, '') || 'No synopsis available.'}
          </motion.p>

          <motion.div key={`btns-${active}`} className="sf-hero-btns"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .4, delay: .16 }}>
            <button className="sf-btn sf-btn-play" onClick={() => onPlay(s)}>
              <Play size={13} /> Play Trailer
            </button>
            <button className="sf-btn sf-btn-outline" style={{ color: accent, borderColor: `${accent}50`, '--btn-glow': glow }} onClick={onWatchlist}>
              <Plus size={13} /> Watchlist
            </button>
            <button className="sf-btn sf-btn-more" onClick={() => onInfo(s)}>
              More Info <ChevronRight size={12} />
            </button>
          </motion.div>
        </div>

        {/* Thumbnails */}
        <div className="sf-hero-thumbs">
          {slides.map((sl, i) => {
            const isA = i === active;
            const slAccent = mode === 'east' ? '#dc2626' : '#06b6d4';
            return (
              <div key={sl.id} className={`sf-thumb${isA ? ' sf-thumb-active' : ''}`}
                onClick={() => switchTo(i)} style={{ borderColor: isA ? `${slAccent}45` : 'rgba(255,255,255,.07)' }}>
                <div className="sf-thumb-top-line" style={{ background: isA ? slAccent : 'transparent' }} />
                <img src={sl.poster} alt={sl.title} loading="lazy" />
                <div className="sf-thumb-label" style={{ color: slAccent }}>
                  {sl.type?.toUpperCase()}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots */}
      <div className="sf-dots">
        {slides.map((_, i) => (
          <div key={i} className={`sf-dot${i === active ? ' sf-dot-active' : ''}`}
            style={{ width: i === active ? '22px' : '6px' }}
            onClick={() => switchTo(i)} />
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   EAST/WEST TOGGLE
═══════════════════════════════════════════════════════════════════ */
const Toggle = ({ value, onChange }) => {
  const isEast = value === 'east';
  return (
    <div className="sf-toggle-wrap">
      <button
        className={cx('sf-toggle-btn', isEast && 'sf-toggle-btn-active-east')}
        onClick={() => onChange('east')}>
        <Globe size={13} /> EAST — Anime
      </button>
      <button
        className={cx('sf-toggle-btn', !isEast && 'sf-toggle-btn-active-west')}
        onClick={() => onChange('west')}>
        <Film size={13} /> WEST — Movies & TV
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   SECTION HEADER
═══════════════════════════════════════════════════════════════════ */
const SH = ({ label, icon: Icon, accent }) => (
  <div className="sf-sh">
    <div className="sf-sh-left">
      <div className="sf-sh-bar" style={{ background: `linear-gradient(to bottom,${accent},transparent)` }} />
      {Icon && <Icon size={13} color={accent} />}
      <span className="sf-sh-label" style={{ color: accent }}>{label}</span>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   POSTER CARD
═══════════════════════════════════════════════════════════════════ */
const PosterCard = ({ item, onPlay, isGrid = false }) => (
  <div className="sf-poster" onClick={() => item.trailerUrl && onPlay(item)}>
    <div className="sf-poster-inner">
      <img className="sf-poster-img" src={item.poster} alt={item.title} loading="lazy" />
      <div className="sf-poster-grad" />
      {item.trailerUrl && (
        <div className="sf-poster-play">
          <div className="sf-poster-play-btn">
            <Play size={16} color="#07070b" />
          </div>
        </div>
      )}
      {item.rating > 0 && (
        <div style={{ position: 'absolute', top: '8px', right: '8px', display: 'flex', alignItems: 'center', gap: '3px', background: 'rgba(0,0,0,.65)', borderRadius: '100px', padding: '3px 7px', fontSize: '9px', fontFamily: 'var(--sf-ui)' }}>
          <Star size={9} color="#fbbf24" />{item.rating}
        </div>
      )}
    </div>
    <div className="sf-poster-body">
      <p className="sf-poster-title">{item.title}</p>
      <div className="sf-poster-meta">
        <span>{item.year}</span>
        {item.type && <><span>·</span><span style={{ textTransform: 'capitalize' }}>{item.type}</span></>}
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   CAROUSEL
═══════════════════════════════════════════════════════════════════ */
const Carousel = ({ items, onPlay, speed = 34 }) => {
  const trackRef = useRef(null);
  const paused = useRef(false);

  useEffect(() => {
    const el = trackRef.current;
    if (!el || !items.length) return;
    let last = performance.now();
    let raf;
    const step = (now) => {
      const dt = (now - last) / 1000; last = now;
      if (!paused.current) {
        el.scrollLeft += speed * dt;
        if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft -= el.scrollWidth / 2;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [items, speed]);

  const handlers = useSwipeable({
    onSwipedLeft:  () => trackRef.current?.scrollBy({ left:  280, behavior: 'smooth' }),
    onSwipedRight: () => trackRef.current?.scrollBy({ left: -280, behavior: 'smooth' }),
    trackMouse: true,
  });

  const loop = useMemo(() => [...items, ...items], [items]);

  return (
    <div className="sf-carousel-root" {...handlers}
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
    >
      <div className="sf-carousel-track" ref={trackRef}>
        {loop.map((item, i) => (
          <PosterCard key={`${item.id}-${i}`} item={item} onPlay={onPlay} />
        ))}
      </div>
      <div className="sf-carousel-fade-l" />
      <div className="sf-carousel-fade-r" />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   SKELETON LOADERS
═══════════════════════════════════════════════════════════════════ */
const CarouselSkeleton = () => (
  <div style={{ display: 'flex', gap: '10px', overflow: 'hidden', marginBottom: '8px' }}>
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="sf-skeleton" style={{ width: 'clamp(140px,18vw,200px)', flexShrink: 0, aspectRatio: '2/3', borderRadius: '12px' }} />
    ))}
  </div>
);

const LoadingState = () => (
  <div style={{ paddingTop: '48px' }}>
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} style={{ marginBottom: '48px' }}>
        <div className="sf-skeleton" style={{ width: '180px', height: '16px', marginBottom: '20px', borderRadius: '8px' }} />
        <CarouselSkeleton />
      </div>
    ))}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════ */
const StreamfinitePage = () => {
  const [mode, setMode] = useState('east');
  const data = useStreamData(mode);
  const { trending, upcoming, topRated, recommended, loading, error, refresh } = data;

  // Per-mode stable data so the hero doesn't flash old content with the new theme
  const [stableDataByMode, setStableDataByMode] = useState({
    east: { trending: [], upcoming: [], topRated: [], recommended: [] },
    west: { trending: [], upcoming: [], topRated: [], recommended: [] }
  });

  const [committedMode, setCommittedMode] = useState('east');

  useEffect(() => {
    if (!loading && (trending.length > 0 || upcoming.length > 0 || topRated.length > 0)) {
      setStableDataByMode(prev => ({
        ...prev,
        [mode]: { trending, upcoming, topRated, recommended }
      }));
      setCommittedMode(mode);
    }
  }, [loading, mode, trending, upcoming, topRated, recommended]);
  const [trailer, setTrailer] = useState({ open: false, item: null });
  const [infoModal, setInfoModal] = useState({ open: false, item: null });
  const [watchlistOpen, setWatchlistOpen] = useState(false);

  const onPlay = useCallback((item) => {
    if (item?.trailerUrl) {
      setTrailer({ open: true, item });
    } else if (item?.title) {
      // Fallback: Open YouTube search for the title if no trailer URL available
      const searchQuery = encodeURIComponent(item.title + ' ' + (item.type || '') + ' trailer');
      const fallbackUrl = `https://www.youtube.com/results?search_query=${searchQuery}`;
      setTrailer({ open: true, item: { ...item, trailerUrl: fallbackUrl } });
    }
  }, []);

  const onInfo = useCallback((item) => {
    setInfoModal({ open: true, item });
  }, []);

  const onWatchlist = useCallback(() => {
    setWatchlistOpen(true);
  }, []);

  const accent = committedMode === 'east' ? '#dc2626' : '#06b6d4';

  return (
    <>
      <GlobalStyles />
      <SEO
        title="Streamfinite — Trending Anime & Movies | Animac"
        description="Discover trending anime, upcoming blockbusters, and top-rated shows. Watch real trailers from AniList, TMDB & more."
        url="/streamfinite"
        image="/assets/streamfinite icon.png"
        type="website"
      />

      <div className="sf-root">

        {/* ── HERO ── */}
        {/* Use stable data from the committed mode so we don't show old East content with the new West (blue) theme */}
        <SFHero 
          items={stableDataByMode[committedMode].upcoming.length > 0 
            ? stableDataByMode[committedMode].upcoming 
            : stableDataByMode[committedMode].trending} 
          mode={committedMode} 
          onPlay={onPlay} 
          onInfo={onInfo} 
          onWatchlist={onWatchlist} 
        />

        {/* ── TOGGLE ── */}
        <div style={{ padding: 'clamp(28px,4vh,44px) 0 0', position: 'relative', zIndex: 1 }}>
          <Toggle value={mode} onChange={(m) => { setMode(m); }} />
        </div>

        <div className="sf-wrap" style={{ position: 'relative', zIndex: 1 }}>

          {/* notice */}
          {error && (
            <div className="sf-notice" style={{ marginTop: '24px' }}>
              <Sparkles size={14} color="#eab308" />
              <span>Some content sources are temporarily unavailable. Showing cached results.</span>
              <button onClick={refresh} style={{ marginLeft: 'auto', background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.1)', color: 'rgba(255,255,255,.7)', padding: '4px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '11px', fontFamily: 'var(--sf-ui)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <RefreshCw size={12} /> Retry
              </button>
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div key={`mode-${mode}`}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: .35 }}>

              {loading ? <LoadingState /> : (
                <>
                  {/* TRENDING */}
                  {trending.length > 0 && (
                    <section className="sf-sec">
                      <SH label="Trending Now" icon={TrendingUp} accent={accent} />
                      <Carousel items={trending} onPlay={onPlay} speed={38} />
                    </section>
                  )}

                  <div className="sf-div" />

                  {/* UPCOMING */}
                  {upcoming.length > 0 && (
                    <section className="sf-sec-sm">
                      <SH label="New & Upcoming" icon={Sparkles} accent={accent} />
                      <Carousel items={upcoming} onPlay={onPlay} speed={44} />
                    </section>
                  )}

                  <div className="sf-div" />

                  {/* TOP RATED */}
                  {topRated.length > 0 && (
                    <section className="sf-sec-sm">
                      <SH label="Top Rated" icon={Star} accent={accent} />
                      <Carousel items={topRated} onPlay={onPlay} speed={36} />
                    </section>
                  )}

                  <div className="sf-div" />

                  {/* RECOMMENDED GRID */}
                  {recommended.length > 0 && (
                    <section className="sf-rec-section" style={{ width: '100%', padding: 'clamp(32px,5vh,56px) 16px', marginLeft: 'calc(-1 * 16px)', marginRight: 'calc(-1 * 16px)' }}>
                      <div style={{ maxWidth: '1280px', margin: '0 auto', width: '100%' }}>
                        <SH label="Recommended For You" icon={Crown} accent={accent} />
                        <div className="sf-rec-grid-full" style={{ width: '100%' }}>
                          {recommended.map((item) => (
                            <PosterCard key={item.id} item={item} onPlay={onPlay} isGrid />
                          ))}
                        </div>
                      </div>
                    </section>
                  )}
                </>
              )}

            </motion.div>
          </AnimatePresence>

          {/* ── STREAMFINITE PREVIEW (tied to east/west toggle) ── */}
          {mode === 'east' && (
            <section className="sf-sec">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{ width: '3px', height: '22px', background: 'linear-gradient(to bottom, #dc2626, transparent)', borderRadius: '2px' }} />
                <Flame size={14} color="#dc2626" />
                <span style={{ fontFamily: 'var(--sf-az)', fontSize: '12px', letterSpacing: '2px', color: '#dc2626', textTransform: 'uppercase' }}>
                  East • Anime Trailers
                </span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: '16px', overflow: 'hidden' }}>
                <StreamfinitePreview filter="anime" />
              </div>
            </section>
          )}

          {mode === 'west' && (
            <section className="sf-sec">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{ width: '3px', height: '22px', background: 'linear-gradient(to bottom, #06b6d4, transparent)', borderRadius: '2px' }} />
                <Film size={14} color="#06b6d4" />
                <span style={{ fontFamily: 'var(--sf-az)', fontSize: '12px', letterSpacing: '2px', color: '#06b6d4', textTransform: 'uppercase' }}>
                  West • Movie Trailers
                </span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(6,182,212,0.25)', borderRadius: '16px', overflow: 'hidden' }}>
                <StreamfinitePreview filter="movie" />
              </div>
            </section>
          )}

          {/* ── STREAMFINITE PORTAL ── */}
          <section className="sf-sec">
            <div className="sf-portal-section" style={{ display: 'flex', justifyContent: 'center' }}>
              <div className="sf-portal-card" style={{ maxWidth: '640px', width: '100%' }}>
                <img className="sf-portal-card-img" src="https://i.pinimg.com/1200x/17/cf/72/17cf72663ad6e3d1c23db253b01fe6dd.jpg" alt="Streamfinite" />
                <div className="sf-portal-card-grad" />
                <div className="sf-portal-card-content">
                  <div className="sf-portal-card-label">Explore More With</div>
                  <h2 className="sf-portal-card-title">Streamfinite</h2>
                  <button className="sf-portal-card-btn">
                    Coming Soon <ChevronRight size={12} />
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* ── FOOTER ── */}
          <div className="sf-div" />
          <div style={{ textAlign: 'center', paddingBottom: '60px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <Link to="/outrange/east" style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 20px', borderRadius: '9px', textDecoration: 'none', border: '1px solid rgba(220,38,38,.3)', color: '#dc2626', fontSize: '10px', fontFamily: 'var(--sf-az)', letterSpacing: '1.5px', textTransform: 'uppercase', transition: 'all .25s' }}>
                <Flame size={12} /> East Portal <ChevronRight size={12} />
              </Link>
              <Link to="/outrange/west" style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 20px', borderRadius: '9px', textDecoration: 'none', border: '1px solid rgba(6,182,212,.3)', color: '#06b6d4', fontSize: '10px', fontFamily: 'var(--sf-az)', letterSpacing: '1.5px', textTransform: 'uppercase', transition: 'all .25s' }}>
                <Film size={12} /> West Portal <ChevronRight size={12} />
              </Link>
              <button onClick={refresh} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 20px', borderRadius: '9px', background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)', color: 'rgba(255,255,255,.4)', fontSize: '10px', fontFamily: 'var(--sf-az)', letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', transition: 'all .25s' }}>
                <RefreshCw size={11} /> Refresh
              </button>
            </div>
            <p style={{ fontSize: '10px', color: 'rgba(255,255,255,.18)', fontFamily: 'var(--sf-ui)', letterSpacing: '.5px' }}>
              Powered by AniList · Jikan · TMDB · Trakt &mdash; supply API keys in .env
            </p>
          </div>

        </div>
      </div>

      <TrailerModal
        open={trailer.open}
        onClose={() => setTrailer({ open: false, item: null })}
        title={trailer.item?.title}
        trailerUrl={trailer.item?.trailerUrl}
      />

      {/* ── INFO MODAL ── */}
      <AnimatePresence>
        {infoModal.open && (
          <motion.div className="sf-info-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={(e) => { if (e.target === e.currentTarget) setInfoModal({ open: false, item: null }); }}>
            <motion.div className="sf-info-box"
              initial={{ scale: .9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: .95, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 220, damping: 22 }}>
              <img className="sf-info-img" src={infoModal.item?.poster || infoModal.item?.backdrop} alt={infoModal.item?.title} />
              <button className="sf-info-close" onClick={() => setInfoModal({ open: false, item: null })} aria-label="Close">
                <X size={14} color="rgba(255,255,255,.8)" />
              </button>
              <div className="sf-info-body">
                <h3 className="sf-info-title">{infoModal.item?.title}</h3>
                <div className="sf-info-meta">
                  {infoModal.item?.rating > 0 && (
                    <span className="sf-info-badge"><Star size={10} color="#fbbf24" />{infoModal.item?.rating}</span>
                  )}
                  <span className="sf-info-badge">{infoModal.item?.year}</span>
                  <span className="sf-info-badge" style={{ textTransform: 'capitalize' }}>{infoModal.item?.type}</span>
                </div>
                <p className="sf-info-desc">
                  {infoModal.item?.synopsis?.replace(/<[^>]*>/g, '')?.slice(0, 150) || 'No synopsis available for this title.'}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── WATCHLIST COMING SOON PANEL ── */}
      <AnimatePresence>
        {watchlistOpen && (
          <motion.div className="sf-wl-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={(e) => { if (e.target === e.currentTarget) setWatchlistOpen(false); }}>
            <motion.div className="sf-wl-box"
              initial={{ scale: .9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: .95, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 220, damping: 22 }}>
              <button className="sf-wl-close" onClick={() => setWatchlistOpen(false)} aria-label="Close">
                <X size={12} color="rgba(255,255,255,.6)" />
              </button>
              <div className="sf-wl-icon">
                <Plus size={28} color="#06b6d4" />
              </div>
              <h3 className="sf-wl-title">Coming Soon</h3>
              <p className="sf-wl-msg">
                The official Streamfinite website is launching soon!<br />
                Stay tuned for exclusive content and features.
              </p>
              <button className="sf-wl-btn" onClick={() => setWatchlistOpen(false)}>
                Got it <ChevronRight size={12} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default StreamfinitePage;